import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useFetch, usePut, usePost, notify, requestHandler } from '../../../components/component-items/helpers'
import { globalState } from '../../../store'

// Components
import GlobalStyles from '../../../components/component-items/styles'
import { Badge, Form, Col, Modal, Row } from 'react-bootstrap'
import { CustomDateInput } from '../../../components/component-items/custom-date-input'
import { Range } from 'react-date-range'

// Types
import {
  Shipment,
  ShipmentBillingMethod,
  ContractingParty,
  ContractingPartyOptions,
  TransportMode,
  TransportModeOptions,
  isNonParcelMode,
  isWillCall,
} from '../../../types/transport'
import { OrderStatus } from '../../../enums'

// Styled
import styled from 'styled-components'

const SectionHeader = styled.div`
  font-weight: 600;
  font-size: 0.95rem;
  margin-bottom: 0.75rem;
  margin-top: 0.25rem;
  color: #333;
`

// ─── Types ───

type HandlingUnitDetail = {
  handlingUnitId: number
  tracking: string
  amount: number | string
  items: any[]
  markup: number | string
  voided: boolean
  serviceLevel: string | number
}

type ServiceLevel = {
  id: number
  carrier__name: string
  name: string
}

type CarrierServiceLevel = {
  id: number
  name: string
}

type CarrierOption = {
  id: number
  name: string
  service_levels: CarrierServiceLevel[]
}

type ShipmentManagementFormProps = {
  shipment: Shipment | null
  handlingUnitDetails: HandlingUnitDetail[]
  serviceLevels: ServiceLevel[]
  orderId: string
  onSubmitSuccess: () => void
  /** B2B order data (merged from logistics modal) */
  b2b?: boolean
  shipBefore?: string | null
  shipAfter?: string | null
  /** Order status ID — form is read-only when >= Shipped (200) */
  statusId?: number
  /** When true, the form renders its own <Form> wrapper and submit button (for full-page mode) */
  standalone?: boolean
  /** When true with standalone, renders the <Form> wrapper but hides the submit button */
  hideSubmitButton?: boolean
  /** Exposed ref to trigger submit from parent (for modal mode) */
  submitRef?: React.MutableRefObject<(() => Promise<void>) | null>
  /** Exposed ref to check loading state from parent */
  loadingRef?: React.MutableRefObject<boolean>
  /** Callback to notify parent when loading state changes */
  onLoadingChange?: (loading: boolean) => void
  /** Callback to notify parent when validated state changes (for modal Form) */
  onValidatedChange?: (validated: boolean) => void
  /** Exposed ref so parent (modal) can wire formRef for checkValidity */
  formElRef?: React.MutableRefObject<HTMLFormElement | null>
  /** When false, warehouse-only sections (BOL/PRO, Billing, Tracking & Cost) are hidden */
  isWarehouse?: boolean
}

type ShipmentManagementModalProps = {
  open: boolean
  setOpen: (open: boolean) => void
  shipment: Shipment | null
  handlingUnitDetails: HandlingUnitDetail[]
  serviceLevels: ServiceLevel[]
  orderId: string
  onUpdate: () => void
  /** B2B order data */
  b2b?: boolean
  shipBefore?: string | null
  shipAfter?: string | null
  /** Order status ID — form is read-only when >= Shipped (200) */
  statusId?: number
  /** When false, warehouse-only sections (BOL/PRO, Billing, Tracking & Cost) are hidden */
  isWarehouse?: boolean
}

// ─── Shared Form Component ───

/**
 * Consolidated shipment management form.
 *
 * Section 1: Transport mode, shipment ID, carrier
 *   -> All save to PATCH /api/shipment/shipments/{id}/
 *
 * Section 2: Shipment identifiers (BOL, PRO) - shown for LTL/TL only
 *   -> Saves to PATCH /api/shipment/shipments/{id}/
 *
 * Section 3: Billing (method, account, bill-to address)
 *   -> Saves to PATCH /api/shipment/shipments/{id}/
 *
 * Section 4: B2B / Logistics (b2b toggle, ship dates)
 *   -> Saves to PUT /api/order/{id}/
 *
 * Section 5: Tracking & cost
 *   -> Saves to POST /api/order/{id}/freight_details/
 *
 * Used by both ShipmentManagementModal (order detail page) and
 * the full-page ShipmentDetails component (packing workflow).
 */
export const ShipmentManagementForm = ({
  shipment,
  handlingUnitDetails,
  serviceLevels,
  orderId,
  onSubmitSuccess,
  b2b: initialB2b = false,
  shipBefore: initialShipBefore = '',
  shipAfter: initialShipAfter = '',
  statusId,
  standalone = false,
  hideSubmitButton = false,
  submitRef,
  loadingRef,
  onLoadingChange,
  onValidatedChange,
  formElRef,
  isWarehouse = true,
}: ShipmentManagementFormProps) => {
  const {
    state: { csrf },
  } = globalState()

  const formRef = useRef<HTMLFormElement | null>(null)

  // ── Section 1: Transport mode & carrier ──
  const [transportMode, setTransportMode] = useState<TransportMode>('parcel')
  const [carrier, setCarrier] = useState<string>('')
  const [carriers, setCarriers] = useState<CarrierOption[]>([])

  // ── Section 2 & 3: Shipment metadata state ──
  const [bolNumber, setBolNumber] = useState('')
  const [proNumber, setProNumber] = useState('')
  const [billingMethod, setBillingMethod] = useState<ShipmentBillingMethod | ''>('')
  const [billingAccount, setBillingAccount] = useState<number | null>(null)
  const [contractingParty, setContractingParty] = useState<ContractingParty | ''>('shipper')

  // ── Shipping accounts for billing dropdown ──
  type ShippingAccountOption = { id: number; carrier__name: string; billing_account: string }
  const [shippingAccounts, setShippingAccounts] = useState<ShippingAccountOption[]>([])
  const [hasCustomerLabels, setHasCustomerLabels] = useState(false)

  // ── Section 4: B2B / Logistics ──
  const [b2bOrder, setB2bOrder] = useState(initialB2b)
  const [shipAfterDate, setShipAfterDate] = useState<Date | null>(
    initialShipAfter ? new Date(initialShipAfter.replace(/-/g, '/')) : null
  )
  const [shipBeforeDate, setShipBeforeDate] = useState<Date | null>(
    initialShipBefore ? new Date(initialShipBefore.replace(/-/g, '/')) : null
  )

  // ── Section 5: Tracking & cost state (single row) ──
  const [tracking, setTracking] = useState('')
  const [serviceLevel, setServiceLevel] = useState<string>('')
  const [amount, setAmount] = useState<string>('')
  const [handlingUnitId, setHandlingUnitId] = useState<number | null>(null)

  // ── Derived state ──
  const hasHandlingUnits = Boolean(handlingUnitDetails?.length)
  const readOnly = (statusId ?? 0) >= OrderStatus.Shipped
  const hasLabelPurchased = handlingUnitDetails?.some((hu) => hu.tracking && !hu.voided) ?? false
  const isParcel = transportMode === 'parcel'
  // For parcel: show documentation/carrier sections when customer provides labels
  // or when contracting party is non-shipper (merchant/consignee provides labels)
  const hasCustomerShipping = isParcel && contractingParty !== '' && contractingParty !== 'shipper'
  const showParcelManualSections = isParcel && (hasCustomerLabels || hasCustomerShipping)

  // ── Shared state ──
  const [loading, setLoadingState] = useState(false)
  const [validated, setValidated] = useState(false)

  // Wrapper that syncs loading state to parent via ref and callback
  const setLoading = useCallback(
    (value: boolean) => {
      setLoadingState(value)
      if (loadingRef) loadingRef.current = value
      onLoadingChange?.(value)
    },
    [loadingRef, onLoadingChange]
  )

  // Sync validated state and formRef to parent (used by modal wrapper)
  useEffect(() => {
    onValidatedChange?.(validated)
  }, [validated, onValidatedChange])
  useEffect(() => {
    if (formElRef) {
      if (standalone) {
        // Standalone: push our form ref up to the parent
        formElRef.current = formRef.current
      } else {
        // Modal: pull the parent's form ref down so checkValidity() works
        formRef.current = formElRef.current
      }
    }
  }, [formElRef, standalone])

  // ── Carrier ↔ Service Level coupling ──
  // Build a lookup from service-level ID → carrier ID so we can auto-set carrier
  // when a service level is selected.
  const slToCarrierMap = useMemo(() => {
    const map: Record<string, string> = {}
    carriers.forEach((c) => {
      c.service_levels?.forEach((sl) => {
        map[String(sl.id)] = String(c.id)
      })
    })
    return map
  }, [carriers])

  // When a carrier is selected, show only that carrier's service levels.
  // When no carrier is selected, fall back to the full serviceLevels prop
  // (which comes from the order-status endpoint, grouped by carrier__name).
  const filteredServiceLevels = useMemo(() => {
    if (!carrier) return null // null = use the prop-based grouped display
    const selected = carriers.find((c) => String(c.id) === carrier)
    return selected?.service_levels ?? []
  }, [carrier, carriers])

  // Carrier grouping for unfiltered service level dropdown (fallback)
  const carrierGrouping = useMemo(() => [...new Set(serviceLevels?.map((sl) => sl.carrier__name))], [serviceLevels])

  // Handler: when carrier changes, clear service level if it doesn't belong
  const handleCarrierChange = useCallback(
    (newCarrier: string) => {
      setCarrier(newCarrier)
      if (newCarrier && serviceLevel) {
        // Check if current service level belongs to the new carrier
        if (slToCarrierMap[serviceLevel] !== newCarrier) {
          setServiceLevel('')
        }
      }
    },
    [serviceLevel, slToCarrierMap]
  )

  // Handler: when service level changes, auto-set carrier if empty or mismatched
  const handleServiceLevelChange = useCallback(
    (newSL: string) => {
      setServiceLevel(newSL)
      if (newSL && slToCarrierMap[newSL]) {
        const matchedCarrier = slToCarrierMap[newSL]
        if (!carrier || carrier !== matchedCarrier) {
          setCarrier(matchedCarrier)
        }
      }
    },
    [carrier, slToCarrierMap]
  )

  // ── Fetch carrier list (warehouse only) ──
  const { response: carrierResp }: any = useFetch(isWarehouse ? '/api/shipment/carrier/' : '', {})
  useEffect(() => {
    if (Array.isArray(carrierResp)) setCarriers(carrierResp)
  }, [carrierResp])

  // ── Fetch shipping accounts for billing dropdown ──
  const { response: shippingResp }: any = useFetch(`/api/order/${orderId}/shipping/`, {})
  useEffect(() => {
    if (shippingResp?.shippingAccounts && Array.isArray(shippingResp.shippingAccounts)) {
      setShippingAccounts(shippingResp.shippingAccounts)
    }
    if (Array.isArray(shippingResp?.customerLabels)) {
      setHasCustomerLabels(shippingResp.customerLabels.length > 0)
    }
  }, [shippingResp])

  // ── Populate form when data changes ──
  useEffect(() => {
    if (shipment) {
      setTransportMode(shipment.transport_mode || 'parcel')
      setCarrier(shipment.carrier_id ? String(shipment.carrier_id) : '')
      setBolNumber(shipment.bill_of_lading_number || '')
      setProNumber(shipment.pro_number || '')
      setBillingMethod(shipment.billing_method || 'prepaid')
      setBillingAccount(shipment.billing_account ?? null)
      setContractingParty(shipment.contracting_party || 'shipper')
    }
  }, [shipment])

  // Populate B2B fields from props
  useEffect(() => {
    setB2bOrder(initialB2b)
  }, [initialB2b])

  useEffect(() => {
    setShipAfterDate(initialShipAfter ? new Date(initialShipAfter.replace(/-/g, '/')) : null)
  }, [initialShipAfter])

  useEffect(() => {
    setShipBeforeDate(initialShipBefore ? new Date(initialShipBefore.replace(/-/g, '/')) : null)
  }, [initialShipBefore])

  useEffect(() => {
    if (handlingUnitDetails?.length) {
      const first = handlingUnitDetails[0]
      setHandlingUnitId(first.handlingUnitId)
      setTracking(first.tracking || '')
      setServiceLevel(first.serviceLevel ? String(first.serviceLevel) : '')
      setAmount(first.amount ? String(first.amount) : '')
    }
  }, [handlingUnitDetails])

  // ── Format date for API ──
  const formatDate = (d: Date | null): string | null => {
    if (!d) return null
    return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`
  }

  // ── Submit handler: saves to multiple endpoints ──
  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    setValidated(true)

    if (formRef.current?.checkValidity() === false) {
      return
    }

    setLoading(true)

    try {
      let allSuccess = true
      let didSave = false

      // 1. Save shipment metadata (transport mode, freight details, billing, carrier)
      if (shipment) {
        didSave = true
        const shipmentPayload: Record<string, unknown> = {
          transport_mode: transportMode,
          bill_of_lading_number: bolNumber || null,
          pro_number: proNumber || null,
          billing_method: billingMethod || null,
          billing_account: billingMethod === 'third_party' ? billingAccount : null,
          carrier: carrier ? parseInt(carrier) : null,
          contracting_party: contractingParty || 'shipper',
        }

        const resp = await requestHandler(
          `/api/shipment/shipments/${shipment.id}/`,
          shipmentPayload,
          csrf,
          false,
          true,
          true, // return_response — get raw Response so we can parse validation errors
          true,
          'PATCH'
        )

        if (!resp) {
          setLoading(false)
          return
        }

        // requestHandler returns the raw Response when return_response=true
        if (resp instanceof Response) {
          if (!resp.ok) {
            try {
              const errorData = await resp.json()
              // If the response has a direct error message, use it; otherwise format field errors
              const messages = errorData.error
                ? String(errorData.error)
                : Object.entries(errorData)
                    .map(([field, msgs]) => {
                      const label = field.replace(/_/g, ' ')
                      const msgText = Array.isArray(msgs) ? msgs.join(', ') : String(msgs)
                      return `${label}: ${msgText}`
                    })
                    .join('\n')
              notify({
                type: 'error',
                title: 'Shipment Validation Error',
                message: messages || 'Failed to save shipment',
              })
            } catch {
              notify({ type: 'error', title: 'Shipment Save Failed', message: `Unexpected error (${resp.status})` })
            }
            allSuccess = false
            setLoading(false)
            return
          }
        } else if (resp.success === false) {
          setLoading(false)
          return
        }
      }

      // 2. Save B2B / logistics data (always applies — order-level fields)
      {
        const logisticsPayload: Record<string, unknown> = {
          b_to_b: b2bOrder,
          ship_before: formatDate(shipBeforeDate),
          ship_after: formatDate(shipAfterDate),
        }
        const resp = await usePut(`/api/order/${orderId}/`, logisticsPayload, csrf, false, true, true)
        if (resp) {
          didSave = true
        } else {
          allSuccess = false
          notify({ type: 'error', message: 'Failed to update order details' })
        }
      }

      // 3. Save tracking/cost (non-shipper parcel + any parcel with customer labels)
      if (!isNonParcelMode(transportMode) && (hasCustomerShipping || hasCustomerLabels) && tracking.trim()) {
        didSave = true
        // Use the first handling unit ID if we have one, otherwise skip
        const targetHandlingUnitId =
          handlingUnitId || (handlingUnitDetails?.length ? handlingUnitDetails[0].handlingUnitId : null)
        if (targetHandlingUnitId !== null) {
          const trackingPayload = {
            handling_units: [
              {
                handlingUnitId: targetHandlingUnitId,
                tracking: tracking.trim(),
                serviceLevel: serviceLevel || '',
                amount: amount || '0',
              },
            ],
          }

          const resp = await usePost(`/api/order/${orderId}/freight_details/`, trackingPayload, csrf, false, true, true)

          if (!resp || resp.success === 'false') {
            allSuccess = false
            notify({ type: 'error', message: resp?.error || 'Failed to update tracking details' })
          }
        }
      }

      if (!didSave) {
        notify({ type: 'warning', message: 'No changes to save' })
        return
      }

      if (allSuccess) {
        notify({ type: 'success', message: 'Shipment details updated successfully' })
        onSubmitSuccess()
      }
    } catch (error) {
      notify({ type: 'error', message: 'Failed to update shipment details' })
    } finally {
      setLoading(false)
    }
  }

  // Expose submit to parent via ref.
  // Intentionally re-runs every render so the ref always points at the latest
  // handleSubmit closure (which captures current form state).
  useEffect(() => {
    if (submitRef) submitRef.current = handleSubmit
  })

  // When standalone, render a <Form> so the component handles its own submission.
  // When embedded in the modal, render a plain <div> to avoid nested <form> elements
  // (the modal wrapper provides its own <Form>).
  const Wrapper = standalone ? Form : 'div'
  const wrapperProps = standalone ? { noValidate: true, ref: formRef, validated, onSubmit: handleSubmit } : {}

  return (
    <Wrapper {...(wrapperProps as any)}>
      {/* ── Shipment (locked after label purchased) ── */}
      <fieldset disabled={hasLabelPurchased}>
        <SectionHeader>Shipment</SectionHeader>
        <Row>
          <Form.Group as={Col} md="6" controlId="orderType">
            <Form.Label>Order Type</Form.Label>
            <Form.Select value={b2bOrder ? 'b2b' : 'dtc'} onChange={(e) => setB2bOrder(e.target.value === 'b2b')}>
              <option value="dtc">DTC (Direct to Consumer)</option>
              <option value="b2b">B2B / Wholesale</option>
            </Form.Select>
          </Form.Group>
          <Form.Group as={Col} md="6" controlId="transportMode">
            <Form.Label>Transport Mode</Form.Label>
            <Form.Select value={transportMode} onChange={(e) => setTransportMode(e.target.value as TransportMode)}>
              {TransportModeOptions.map((option) => (
                <option value={option.value} key={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Row>
      </fieldset>

      {/* ── Documentation (hidden for will_call and standard parcel — EasyPost manages labels) ── */}
      {!isWillCall(transportMode) && (!isParcel || showParcelManualSections) && (
        <>
          <hr style={{ margin: '0.5rem 0 1rem' }} />
          <SectionHeader>Documentation</SectionHeader>
          <Row>
            {/* Tracking Number: parcel only */}
            {transportMode === 'parcel' && (
              <Form.Group as={Col} md="6">
                <Form.Label>Tracking Number</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Tracking #"
                  value={tracking}
                  onChange={(e) => setTracking(e.target.value)}
                  disabled={!hasHandlingUnits}
                  readOnly={!hasCustomerLabels && !hasCustomerShipping}
                />
              </Form.Group>
            )}
            {/* BOL & PRO: LTL/TL only */}
            {isNonParcelMode(transportMode) && (
              <>
                <Form.Group as={Col} md="6">
                  <Form.Label>BOL Number</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Bill of Lading #"
                    pattern="^[A-Za-z0-9\-\s]+$"
                    value={bolNumber}
                    onChange={(e) => setBolNumber(e.target.value)}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    BOL number is required for LTL/TL shipments.
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} md="6">
                  <Form.Label>PRO Number</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="PRO #"
                    pattern="^[A-Za-z0-9\-\s]*$"
                    value={proNumber}
                    onChange={(e) => setProNumber(e.target.value)}
                  />
                  <Form.Control.Feedback type="invalid">
                    PRO number can only contain letters, numbers, dashes, and spaces.
                  </Form.Control.Feedback>
                </Form.Group>
              </>
            )}
          </Row>
        </>
      )}

      {/* ── Carrier & Service (warehouse only; hidden for will_call and standard parcel — EasyPost handles rate shopping) ── */}
      {isWarehouse && !isWillCall(transportMode) && (!isParcel || showParcelManualSections) && (
        <>
          <hr style={{ margin: '0.5rem 0 1rem' }} />
          <SectionHeader>Carrier &amp; Service</SectionHeader>
          <Row style={!hasHandlingUnits ? { opacity: 0.6 } : undefined}>
            <Form.Group as={Col} md="4">
              <Form.Label>Carrier</Form.Label>
              <Form.Select
                value={carrier}
                onChange={(e) => handleCarrierChange(e.target.value)}
                disabled={!hasHandlingUnits}
              >
                <option value="">Select carrier...</option>
                {carriers.map((c) => (
                  <option value={c.id} key={c.id}>
                    {c.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            {!isNonParcelMode(transportMode) && (
              <>
                <Form.Group as={Col} md="4">
                  <Form.Label>Service Level</Form.Label>
                  <Form.Select
                    value={serviceLevel}
                    onChange={(e) => handleServiceLevelChange(e.target.value)}
                    disabled={!hasHandlingUnits}
                  >
                    <option value="">Select service level...</option>
                    {filteredServiceLevels
                      ? filteredServiceLevels.map((sl) => (
                          <option value={sl.id} key={sl.id}>
                            {sl.name}
                          </option>
                        ))
                      : carrierGrouping?.map((c, id) => (
                          <optgroup label={c} key={id}>
                            {serviceLevels
                              .filter((sl) => sl.carrier__name === c)
                              .map((s, idx) => (
                                <option value={s.id} key={idx}>
                                  {s.name}
                                </option>
                              ))}
                          </optgroup>
                        ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group as={Col} md="4">
                  <Form.Label>Cost ($)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    disabled={!hasHandlingUnits}
                  />
                  <Form.Text className="text-muted">Markup is applied automatically.</Form.Text>
                </Form.Group>
              </>
            )}
          </Row>
        </>
      )}

      {/* ── Billing & Control (hidden for will_call; locked after label purchased) ── */}
      {!isWillCall(transportMode) && (
        <>
          <hr style={{ margin: '0.5rem 0 1rem' }} />
          <SectionHeader>Billing &amp; Control</SectionHeader>
          <fieldset disabled={hasLabelPurchased}>
            <Row>
              {/* Contracting party — visible for all billable transport modes */}
              <Form.Group as={Col} md="4">
                <Form.Label>Contracting Party</Form.Label>
                <Form.Select
                  value={contractingParty}
                  onChange={(e) => setContractingParty(e.target.value as ContractingParty)}
                >
                  {ContractingPartyOptions.map((opt) => (
                    <option value={opt.value} key={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group as={Col} md="4" controlId="billingMethod">
                <Form.Label>Billing Method</Form.Label>
                <Form.Select
                  value={billingMethod}
                  onChange={(e) => setBillingMethod(e.target.value as ShipmentBillingMethod | '')}
                >
                  <option value="prepaid">Prepaid</option>
                  <option value="collect">Collect</option>
                  <option value="third_party">Third Party</option>
                </Form.Select>
              </Form.Group>
              {billingMethod === 'third_party' && (
                <Form.Group as={Col} md="4" controlId="billingAccount">
                  <Form.Label>Billing Account</Form.Label>
                  <Form.Select
                    value={billingAccount ?? ''}
                    onChange={(e) => setBillingAccount(e.target.value ? parseInt(e.target.value) : null)}
                    required
                  >
                    <option value="">Select shipping account...</option>
                    {shippingAccounts.map((acct) => (
                      <option value={acct.id} key={acct.id}>
                        {acct.carrier__name} — {acct.billing_account}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    A shipping account is required for third-party billing.
                  </Form.Control.Feedback>
                </Form.Group>
              )}
            </Row>
          </fieldset>
        </>
      )}

      {/* ── Ship Dates (B2B only; locked after shipped) ── */}
      {b2bOrder && (
        <fieldset disabled={readOnly}>
          <hr style={{ margin: '0.5rem 0 1rem' }} />
          <SectionHeader>Ship Dates</SectionHeader>
          <Row>
            <Form.Group as={Col} md="6">
              <Form.Label>Ship After</Form.Label>
              <CustomDateInput
                value={shipAfterDate}
                onChange={(date: Date | Range | null) => setShipAfterDate((date as Date) || null)}
                placeholder="MM-DD-YYYY"
              />
            </Form.Group>
            <Form.Group as={Col} md="6">
              <Form.Label>Ship Before</Form.Label>
              <CustomDateInput
                value={shipBeforeDate}
                onChange={(date: Date | Range | null) => setShipBeforeDate((date as Date) || null)}
                placeholder="MM-DD-YYYY"
              />
            </Form.Group>
          </Row>
        </fieldset>
      )}

      {/* Standalone submit button for full-page mode */}
      {standalone && !hideSubmitButton && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <GlobalStyles.Button type="submit" disabled={loading} style={{ minWidth: 150 }}>
            {loading ? 'Saving...' : 'Update'}
          </GlobalStyles.Button>
        </div>
      )}
    </Wrapper>
  )
}

// ─── Modal Wrapper ───

/**
 * Consolidated shipment management modal for the order detail page.
 * Uses react-bootstrap Modal directly so the Form wraps Header + Body + Footer,
 * matching the AddressModal pattern used elsewhere in the codebase.
 */
export const ShipmentManagementModal = ({
  open,
  setOpen,
  shipment,
  handlingUnitDetails,
  serviceLevels,
  orderId,
  onUpdate,
  b2b,
  shipBefore,
  shipAfter,
  statusId,
  isWarehouse = true,
}: ShipmentManagementModalProps) => {
  const submitRef = useRef<(() => Promise<void>) | null>(null)
  const formElRef = useRef<HTMLFormElement | null>(null)
  const [loading, setLoading] = useState(false)
  const [validated, setValidated] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (submitRef.current) {
      await submitRef.current()
    }
  }

  const handleSuccess = () => {
    onUpdate()
    setOpen(false)
  }

  return (
    <Modal show={open} onHide={() => setOpen(false)} size="lg" aria-labelledby="contained-modal-title-vcenter" centered>
      <Form noValidate validated={validated} ref={formElRef} onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            Shipment Details
            {shipment?.shipment_id && (
              <Badge
                bg="secondary"
                style={{ fontSize: '0.65rem', fontWeight: 500, marginLeft: '0.75rem', verticalAlign: 'middle' }}
              >
                {shipment.shipment_id}
              </Badge>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ShipmentManagementForm
            shipment={shipment}
            handlingUnitDetails={handlingUnitDetails}
            serviceLevels={serviceLevels}
            orderId={orderId}
            onSubmitSuccess={handleSuccess}
            submitRef={submitRef}
            onLoadingChange={setLoading}
            onValidatedChange={setValidated}
            formElRef={formElRef}
            b2b={b2b}
            shipBefore={shipBefore}
            shipAfter={shipAfter}
            statusId={statusId}
            isWarehouse={isWarehouse}
          />
        </Modal.Body>
        <Modal.Footer>
          <GlobalStyles.Button style={{ minWidth: 125 }} disabled={loading}>
            {loading ? 'Saving...' : 'Save'}
          </GlobalStyles.Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}
